import { ReactNode, useContext } from 'react'
import { Field } from 'redux-form'
import { QuestionCircleFilled } from '@ant-design/icons'
import { Input } from 'antd'
import { PARENT_PRODUCT_TOOLTIP } from '@Orders/OrderDetailsPage/constants'
import { OrderContext } from '@Orders/OrderDetailsPage/context'

import Box from '~/core/Box'
import Image from '~/core/Image'
import Tooltip from '~/core/Tooltip'
import TruncatedText from '~/core/TruncatedText'
import { MyInput } from '~/core/FormFields'
import getColor from '~/lib/getColor'
import styles from '../styles.css'
import { salesOrder_salesOrder_salesOrderLines_items as SalesOrderLineItemType } from '../Queries/__generated__/salesOrder'

import { isFieldRequired, normalizeHtsInput } from './HTSLogic'

export interface ColumnPropType {
    title: string | ReactNode
    dataIndex: string | string[]
    key: string
    width?: string
    fixed?: boolean | 'left' | 'right' | undefined
    className: string
    render:
        | ((
              text: string,
              record: SalesOrderLineItemType,
              index: number,
          ) => ReactNode)
        | undefined
}

interface HTSFormColumnsPropsType {
    isAllHtsValuesSubmitted: boolean
    typeOfHts: string
    validationForHtsCode: Function
}

const HTSFormColumns = (
    props: HTSFormColumnsPropsType,
    isHTSSubmissionTimeExpired: boolean,
): ColumnPropType[] => {
    const { isAllHtsValuesSubmitted, validationForHtsCode } = props

    const { isImOrder } = useContext(OrderContext)

    const staticColumns: ColumnPropType[] = [
        {
            title: 'Part name',
            dataIndex: ['revision', 'modelFileName'],
            key: 'modelFileName',
            width: '25%',
            fixed: 'left',
            className: styles.verticalAlignTop,
            render: (text: string, record: SalesOrderLineItemType) => {
                return (
                    <Box>
                        {!isImOrder && (
                            <Image
                                style={{
                                    width: '48px',
                                    height: '48px',
                                    background: 'white',
                                    marginRight: '8px',
                                }}
                                src={record.revision?.thumbnailUri}
                            />
                        )}
                        <TruncatedText
                            maxLength={54}
                            style={{
                                marginTop: '8px',
                                color: `${
                                    isAllHtsValuesSubmitted
                                        ? getColor('grayMedium')
                                        : getColor('blackAlpha85')
                                }`,
                            }}
                        >
                            {text}
                        </TruncatedText>
                    </Box>
                )
            },
        },
        {
            title: (
                <>
                    HTS code (optional)
                    <Tooltip title="10-digit code declaring the category corresponding to this part in order to determine the appropriate customs duty.">
                        <QuestionCircleFilled
                            className={styles.columnQuestion}
                            style={{ marginLeft: '5px' }}
                        />
                    </Tooltip>
                </>
            ),
            dataIndex: 'htsCode',
            key: 'htsCode',
            width: '25%',
            className: styles.verticalAlignTop,
            render: (text: string, record: SalesOrderLineItemType) => {
                if (isAllHtsValuesSubmitted) {
                    return (
                        <Box marginTop="8px" color="grayMedium">
                            {text || '-'}
                        </Box>
                    )
                }

                const item = `${record.revision?.id}:htsCode`

                return (
                    <Field
                        name={item}
                        validate={validationForHtsCode}
                        normalize={normalizeHtsInput}
                        placeholder="1234.56.7890"
                        type="text"
                        data-test-target="htsCode"
                        inputType={Input}
                        component={MyInput}
                        wrapperStyle={{ marginBottom: 0 }}
                        disabled={isHTSSubmissionTimeExpired}
                    />
                )
            },
        },
        {
            title: (
                <>
                    *Part description
                    <Tooltip title="Customs requires a brief description be provided for each part. This should describe what the part is, i.e., 'mounting plate for a scooter'.">
                        <QuestionCircleFilled
                            className={styles.columnQuestion}
                            style={{ marginLeft: '5px' }}
                        />
                    </Tooltip>
                </>
            ),
            dataIndex: 'htsDescription',
            key: 'htsDescription',
            className: styles.verticalAlignTop,
            width: '25%',
            render: (text: string, record: SalesOrderLineItemType) => {
                if (isAllHtsValuesSubmitted) {
                    return (
                        <Box marginTop="8px" color="grayMedium">
                            {text}
                        </Box>
                    )
                }

                const item = `${record.revision?.id}:description`

                return (
                    <Field
                        name={item}
                        type="text"
                        placeholder="i.e., plate for a scooter"
                        data-test-target="htsDescription"
                        component={MyInput}
                        validate={[isFieldRequired]}
                        wrapperStyle={{ marginBottom: 0 }}
                        disabled={isHTSSubmissionTimeExpired}
                    />
                )
            },
        },
        {
            title: (
                <>
                    *Parent product & end-use
                    <Tooltip title={PARENT_PRODUCT_TOOLTIP}>
                        <QuestionCircleFilled
                            className={styles.columnQuestion}
                            style={{ marginLeft: '5px' }}
                        />
                    </Tooltip>
                </>
            ),
            dataIndex: 'htsApplication',
            key: 'htsApplication',
            width: '25%',
            className: styles.verticalAlignTop,
            render: (text: string, record: SalesOrderLineItemType) => {
                if (isAllHtsValuesSubmitted) {
                    return (
                        <Box marginTop="8px" color="grayMedium">
                            {text}
                        </Box>
                    )
                }

                const item = `${record.revision?.id}:application`

                return (
                    <Field
                        name={item}
                        type="text"
                        placeholder="i.e., test part for a scooter prototype"
                        data-test-target="htsApplication"
                        component={MyInput}
                        validate={[isFieldRequired]}
                        wrapperStyle={{ marginBottom: 0 }}
                        disabled={isHTSSubmissionTimeExpired}
                    />
                )
            },
        },
    ]

    return staticColumns
}

export default HTSFormColumns
